import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, Crown, Sparkles, Loader2, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const plans = [
  {
    id: 'india',
    name: 'India',
    price: '₹2,500',
    period: '/month',
    currency: 'INR',
    features: [
      'Unlimited Script Generation',
      'Unlimited Image Generation',
      'Unlimited Audio Generation',
      'Video Generation & Export',
      'YouTube Direct Upload',
      'AI Hashtag Generator',
      'Image Editor with Text Overlay',
      'Content Library',
      'Priority Support'
    ],
    popular: false
  },
  {
    id: 'international',
    name: 'International',
    price: '$50',
    period: '/month',
    currency: 'USD',
    features: [
      'Unlimited Script Generation',
      'Unlimited Image Generation',
      'Unlimited Audio Generation',
      'Video Generation & Export',
      'YouTube Direct Upload',
      'AI Hashtag Generator',
      'Image Editor with Text Overlay',
      'Content Library',
      'Priority Support'
    ],
    popular: true
  }
];

export default function Subscription() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    // Check for success redirect
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      fetchSubscriptionStatus();
    }
  }, [searchParams]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/subscription/status`, getAuthHeader());
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email.');
      navigate('/subscription');
      return;
    }

    setCheckingStatus(true);

    try {
      const response = await axios.get(
        `${API}/api/subscription/checkout/status/${sessionId}`,
        getAuthHeader()
      );

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        setCheckingStatus(false);
        fetchSubscriptionStatus();
        // Clean URL
        window.history.replaceState({}, '', '/subscription');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingStatus(false);
        navigate('/subscription');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingStatus(false);
      toast.error('Error verifying payment. Please contact support.');
    }
  };

  const handleSubscribe = async (planId) => {
    setLoading(true);
    setSelectedPlan(planId);

    try {
      console.log('Starting checkout for plan:', planId);
      const token = localStorage.getItem('session_token');
      console.log('Token present:', !!token);
      
      const response = await axios.post(
        `${API}/api/subscription/checkout`,
        {
          plan: planId,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );

      console.log('Checkout response:', response.data);
      
      if (response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        toast.error('No checkout URL received');
        setLoading(false);
        setSelectedPlan(null);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Card className="bg-zinc-900 border-zinc-800 p-8 text-center max-w-md">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-violet-500" />
          <h2 className="text-xl font-bold text-white mb-2">Verifying Payment...</h2>
          <p className="text-zinc-400">Please wait while we confirm your payment.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <Button
            variant="ghost"
            className="mb-6 text-zinc-400"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <Crown className="w-8 h-8 text-yellow-500" />
            <h1 className="text-4xl font-bold text-white">Choose Your Plan</h1>
          </div>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Unlock the full power of Saketh Studio with unlimited content generation
          </p>

          {/* Current Status */}
          {subscriptionStatus && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800/50 border border-zinc-700">
              {subscriptionStatus.is_admin ? (
                <>
                  <Crown className="w-4 h-4 text-yellow-500" />
                  <span className="text-yellow-500 font-medium">Admin Access</span>
                </>
              ) : subscriptionStatus.subscription_status === 'active' ? (
                <>
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-green-500 font-medium">Active Subscription</span>
                  <span className="text-zinc-500 text-sm">
                    (expires {new Date(subscriptionStatus.end_date).toLocaleDateString()})
                  </span>
                </>
              ) : subscriptionStatus.subscription_status === 'trial' ? (
                <>
                  <Sparkles className="w-4 h-4 text-blue-500" />
                  <span className="text-blue-500 font-medium">Free Trial</span>
                  <span className="text-zinc-500 text-sm">
                    (ends {new Date(subscriptionStatus.trial_end).toLocaleDateString()})
                  </span>
                </>
              ) : (
                <>
                  <span className="text-red-500 font-medium">No Active Subscription</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative bg-zinc-900 border-2 p-8 ${
                plan.popular ? 'border-violet-500' : 'border-zinc-800'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-violet-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-zinc-400">{plan.period}</span>
                </div>
                <p className="text-sm text-zinc-500 mt-1">{plan.currency}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-zinc-300">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loading || subscriptionStatus?.is_admin || subscriptionStatus?.subscription_status === 'active'}
                className={`w-full h-12 text-lg font-semibold ${
                  plan.popular
                    ? 'bg-violet-600 hover:bg-violet-500'
                    : 'bg-zinc-700 hover:bg-zinc-600'
                }`}
              >
                {loading && selectedPlan === plan.id ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : subscriptionStatus?.is_admin ? (
                  'Admin Access'
                ) : subscriptionStatus?.subscription_status === 'active' ? (
                  'Already Subscribed'
                ) : (
                  'Subscribe Now'
                )}
              </Button>
            </Card>
          ))}
        </div>

        {/* Trial Info */}
        <div className="text-center mt-12">
          <p className="text-zinc-500">
            All plans include a <span className="text-violet-400 font-medium">7-day free trial</span> for new users.
          </p>
          <p className="text-zinc-600 text-sm mt-2">
            Cancel anytime. No questions asked.
          </p>
        </div>
      </div>
    </div>
  );
}
